import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindProductCollectionValuesQueryResponse,
  FindProductCollectionValuesQueryParams,
} from "../types/FindProductCollectionValues"

type FindProductCollectionValuesClient = typeof client<
  FindProductCollectionValuesQueryResponse,
  Error,
  never
>
type FindProductCollectionValues = {
  data: FindProductCollectionValuesQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindProductCollectionValuesQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindProductCollectionValuesClient>>
  client: {
    parameters: Partial<Parameters<FindProductCollectionValuesClient>[0]>
    return: Awaited<ReturnType<FindProductCollectionValuesClient>>
  }
}
export const findProductCollectionValuesQueryKey = (
  params: FindProductCollectionValues["queryParams"]
) =>
  [
    { url: "/v1/product/find-product-collection-values" },
    ...(params ? [params] : []),
  ] as const
export type FindProductCollectionValuesQueryKey = ReturnType<
  typeof findProductCollectionValuesQueryKey
>
export function findProductCollectionValuesQueryOptions<
  TData = FindProductCollectionValues["response"],
  TQueryData = FindProductCollectionValues["response"],
>(
  params: FindProductCollectionValues["queryParams"],
  options: FindProductCollectionValues["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindProductCollectionValues["response"],
    FindProductCollectionValues["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findProductCollectionValuesQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindProductCollectionValues["data"],
        FindProductCollectionValues["error"]
      >({
        method: "get",
        url: `/v1/product/find-product-collection-values`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find product collection values
 * @link /v1/product/find-product-collection-values
 */
export function useFindProductCollectionValues<
  TData = FindProductCollectionValues["response"],
  TQueryData = FindProductCollectionValues["response"],
  TQueryKey extends QueryKey = FindProductCollectionValuesQueryKey,
>(
  params: FindProductCollectionValues["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindProductCollectionValues["response"],
        FindProductCollectionValues["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindProductCollectionValues["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindProductCollectionValues["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findProductCollectionValuesQueryKey(params)
  const query = useQuery<
    FindProductCollectionValues["data"],
    FindProductCollectionValues["error"],
    TData,
    any
  >({
    ...findProductCollectionValuesQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindProductCollectionValues["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
